import { Button, Input } from 'components/shared'
import {
    classNameJoin,
    getErrorData,
    getLocalStorageCookie,
    setLocalStorageCookie,
} from 'utils/lib'
import { ReactComponent as CloudIcon } from '../../../assets/cloud.svg'
import { Register } from 'interfaces/user.interface'
import { useRef, useState } from 'react'
import { useConfirmV2 } from 'contexts/ConfirmV2Provider'
import {
    getCheckBusinessNumber,
    postCheckPhone,
    postVerifyCheckCode,
    postVerifySendCode,
} from 'api/user.request'
import { useMutation } from 'react-query'
import { fileUploadNoAuth } from 'api/service.request'
import dayjs from 'dayjs'
import { Footer } from './Footer'

interface StepOneProps {
    item: Register
    callback: (key: keyof Register, value: string) => void
}
interface IPhoneVerify {
    count: string
    expires: string
}

export const StepOne = ({ item, callback }: StepOneProps) => {
    const { confirm } = useConfirmV2()

    // 대표자
    const [businessName, setBusinessName] = useState(item.businessName)
    const [businessNameError, setBusinessError] = useState('')
    // 핸드폰
    const [phone, setPhone] = useState(item.phone)
    const [phoneError, setPhoneError] = useState('')
    const [phoneValid, setPhoneValid] = useState(false)
    const [checkNumber, setCheckNumber] = useState('')
    const [sendCode, setSendCode] = useState(false)

    // 상호 법인 명
    const [representativeName, setRepresentativeName] = useState(item.representativeName)
    const [representativeNameError, setRepresentativeNameError] = useState('')
    // 사업자 번호
    const [businessNumber, setBusinessNumber] = useState(item.businessRegistrationNumber)
    const [businessNumberError, setBusinessNumberError] = useState('')
    const [checkBusinessNumber, setCheckBusinessNumber] = useState(false)
    const businessMutation = useMutation(getCheckBusinessNumber)

    // 파일 업로더
    const uploadMutation = useMutation(fileUploadNoAuth)
    // 업로드 input ref
    const businessFileInputRef = useRef<HTMLInputElement>(null)
    const idCardFileInputRef = useRef<HTMLInputElement>(null)

    const [businessFileName, setBusinessFileName] = useState('')
    const [idCardFileName, setIdCardFileName] = useState('')

    const [businessFileLoading, setBusinessFileLoading] = useState(false)
    const [idCardFileLoading, setIdCardFileLoading] = useState(false)

    const avaliableHandler = async () => {
        setPhoneValid(false)
        if (phone.length < 10) {
            setPhoneError('휴대폰번호를 정상적으로 입력해주세요.')
            return false
        }

        if (/^[0-9]{10,11}$/g.test(phone)) {
            const _ = getLocalStorageCookie<IPhoneVerify | null>(phone)

            if (_ && parseInt(_.count) > 9) {
                confirm({
                    invisibleCancel: true,
                    title: '인증 횟수 초과',
                    description: (
                        <>
                            <div>1일 10회 이상 동일한 번호로</div>
                            <div>인증문자를 발송할 수 없습니다.</div>
                            <div>내일 다시 시도해주세요.</div>
                        </>
                    ),
                })
                return
            }

            try {
                const { data } = await postCheckPhone({ phone, type: 'all' })
                // const { data } = await postCheckPhone({ phone: '', type: 'all' })
                if (data.data) {
                    if (data.data.available) {
                        await postVerifySendCode({ phone })
                        confirm({
                            invisibleCancel: true,
                            title: '인증번호 발송 성공',
                            description: (
                                <>
                                    <div>인증번호가 오지 않으면 입력하신</div>
                                    <div>휴대폰 번호가 맞게 입력되어 있는지</div>
                                    <div>확인해 주세요.</div>
                                </>
                            ),
                            onConfirm() {
                                setSendCode(true)
                                const expires = dayjs().add(1, 'day').format('YYYY-MM-DD 06:00:00')

                                setLocalStorageCookie(
                                    phone,
                                    { count: parseInt(_ ? _.count : '0') + 1 },
                                    expires
                                )
                            },
                        })
                    } else {
                        setPhoneError('이미 사용중인 전화번호 입니다.')
                    }
                } else {
                    setPhoneError('휴대폰번호 중복 검사에 실패 했습니다.')
                }
            } catch (err: any) {
                if (err.code === '100020') {
                    confirm({
                        invisibleCancel: true,
                        title: '인증실패',
                        description: (
                            <>
                                <div>인증 요청은 30초에 한번만 가능합니다.</div>
                                <div>잠시 후 다시 시도해주세요.</div>
                            </>
                        ),
                    })
                    return
                }

                confirm({
                    invisibleCancel: true,
                    title: '인증실패',
                    description: (
                        <>
                            <div>인증 번호 발송에 실패 했습니다.</div>
                            <div>지속적으로 발생한다면 고객센터에 문의해 주세요.</div>
                            <div>({err.code})</div>
                        </>
                    ),
                })
            }
        } else {
            setPhoneError('휴대폰번호를 정상적으로 입력해주세요.')
            return false
        }

        return false
    }

    const checkNumberHandler = async () => {
        if (!phone || !checkNumber) {
            confirm({
                invisibleCancel: true,
                description: '인증번호를 입력해주세요.',
            })
            return
        }
        try {
            await postVerifyCheckCode({ phone, code: checkNumber })
            setPhoneValid(true)
            callback('phone', phone)
        } catch (err: any) {
            confirm({
                invisibleCancel: true,
                title: '인증실패',
                description: err.message,
            })
        }
    }

    const validateBusinessNumber = () => {
        if (!businessNumber) {
            setBusinessNumberError('사업자 등록번호를 입력해주세요.')
            return
        }
        if (businessNumber?.length == 0) {
            setBusinessNumberError('사업자 등록번호를 입력해주세요.')
            return
        }
        if (businessNumber?.length > 0 && !/^[0-9]{10}$/g.test(businessNumber)) {
            setBusinessNumberError('사업자 등록번호를 정상적으로 입력해주세요.')
            return
        }
        // 정상 케이스
        if (businessNumber?.length > 0 && /^[0-9]{10}$/g.test(businessNumber)) {
            setBusinessNumberError('')
        }

        businessMutation.mutate(businessNumber, {
            onSuccess: (res) => {
                const data = res.data.data
                if (!data) {
                    callback('businessRegistrationNumber', businessNumber)
                    setCheckBusinessNumber(true)
                } else {
                    setBusinessNumberError('이미 가압된 사업자 등록번호 입니다.')
                    callback('businessRegistrationNumber', '')
                }
            },
            onError: (err: any) => {
                callback('businessRegistrationNumber', '')
                confirm({
                    invisibleCancel: true,
                    title: '인증실패',
                    description: err.message,
                })
            },
        })
        // 통과
    }

    const uploadHandler = async (files: FileList | null, type: 'business' | 'idcard') => {
        if (!files || files.length === 0) return
        if (type === 'business') {
            setBusinessFileLoading(true)
        } else {
            setIdCardFileLoading(true)
        }
        const formData = new FormData()
        formData.append('files', files[0]) // 1개만 받는다고 가정

        // 5메가가 넘어가면 알럿창 띄움
        if (files[0].size > 5 * 1024 * 1024) {
            if (type === 'business') {
                setBusinessFileLoading(false)
            } else {
                setIdCardFileLoading(false)
            }
            confirm({
                invisibleCancel: true,
                title: '파일 용량 초과',
                description: (
                    <>
                        <div>파일 용량은 5MB를 초과할 수 없습니다.</div>
                        <div>다시 시도해주세요.</div>
                    </>
                ),
            })

            return
        }

        try {
            const { data } = await uploadMutation.mutateAsync(formData)
            const fileId = data.data![0]?.fileId
            if (fileId) {
                const _ = callback as any
                if (type === 'business') {
                    setBusinessFileName(files[0].name)
                    _('businessRegistrationFileId', fileId)
                } else {
                    setIdCardFileName(files[0].name)
                    _('idCardFileId', fileId)
                }
            }
        } catch (e: any) {
            confirm({
                invisibleCancel: true,
                description: e.message,
            })
            if (type === 'business') {
                setBusinessFileName('')
            } else {
                setIdCardFileName('')
            }
        } finally {
            if (type === 'business') {
                setBusinessFileLoading(false)
            } else {
                setIdCardFileLoading(false)
            }
        }
    }
    return (
        <section className="flex flex-col gap-10 w-full items-center">
            <article className="flex flex-col gap-1">
                <h2
                    className={classNameJoin([
                        'text-[clamp(18px,3vw,22px)] font-bold text-[#242424]',
                    ])}
                >
                    사업자 정보를 모두 입력해주세요.
                </h2>
                <p
                    className={classNameJoin([
                        'text-[clamp(14px,3vw,18px)] font-normal text-[#898989]',
                        'break-keep',
                    ])}
                >
                    가입 신청이 완료되면, 입력해주신 휴대폰번호로 전자계약서를 보내드려요.{' '}
                    <b className="font-normal text-[#F52388]">
                        본인인증이 필요하니 꼭 정확하게 입력
                    </b>
                    해주세요.
                </p>
            </article>
            <article className="flex flex-col gap-2 w-full items-start">
                <label className="text-[clamp(14px,3vw,18px)] font-medium text-[#565656]">
                    대표자 성함
                </label>
                <Input
                    type="text"
                    className="w-full h-[clamp(44px,3vw,52px)]"
                    inputClassName={'text-[clamp(14px,3vw,18px)] border-[#c1c1c1]'}
                    placeholder="대표자 성함 입력"
                    value={representativeName}
                    onChange={(e) => {
                        let value = e.target.value
                        value = value.replaceAll(' ', '')
                        if (value.length > 20) {
                            setRepresentativeName(value.slice(0, 20))
                        } else {
                            setRepresentativeName(value)
                        }
                        setRepresentativeNameError('')
                        callback('representativeName', '')
                    }}
                    onBlur={() => {
                        if (!representativeName || representativeName?.length == 0) {
                            setRepresentativeNameError('성함을 입력해주세요.')
                            return
                        }

                        callback('representativeName', representativeName)
                    }}
                />
                {representativeNameError && (
                    <p className="text-[clamp(11px, 3vw, 15px)] text-[#D5342B] px-2">
                        {representativeNameError}
                    </p>
                )}
            </article>
            <article className="flex flex-col gap-2 w-full items-start">
                <label className="text-[clamp(14px,3vw,18px)] font-medium text-[#565656]">
                    대표자 휴대폰 번호
                </label>
                <div className="flex flex-row gap-2 w-full">
                    <Input
                        type="text"
                        className="w-full h-[clamp(44px,3vw,52px)]"
                        inputClassName={'text-[clamp(14px,3vw,18px)] border-[#c1c1c1]'}
                        placeholder="-없이 숫자 숫자만 입력"
                        value={phone}
                        onChange={(e) => {
                            const value = e.target.value
                            const onlyNumber = value.replace(/[^0-9]/g, '')
                            if (onlyNumber.length > 11) {
                                setPhone(onlyNumber.slice(0, 11))
                                return
                            }
                            setPhone(onlyNumber)
                            setPhoneError('')
                            setPhoneValid(false)
                            setCheckNumber('')
                            callback('phone', '')
                            setSendCode(false)
                        }}
                    />
                    <Button
                        ghost={phoneValid}
                        className="w-[17%]  h-full flex items-center justify-center"
                        onClick={avaliableHandler}
                    >
                        <p className="text-[clamp(14px,3vw,20px)]">인증</p>
                    </Button>
                </div>

                {sendCode && (
                    <div className="flex flex-row gap-2 w-full">
                        <Input
                            type="text"
                            className="w-full h-[clamp(44px,3vw,52px)]"
                            inputClassName={'text-[clamp(14px,3vw,18px)] border-[#c1c1c1]'}
                            placeholder="인증번호 입력"
                            value={checkNumber}
                            disabled={phoneValid}
                            onChange={(e) => {
                                const value = e.target.value
                                const onlyNumber = value.replace(/[^0-9]/g, '')
                                setCheckNumber(onlyNumber)
                            }}
                        />
                        <Button
                            ghost
                            className="w-[17%]  h-full flex items-center justify-center"
                            onClick={checkNumberHandler}
                            disabled={phoneValid}
                        >
                            <p className="text-[clamp(14px,3vw,20px)]">
                                {phoneValid ? '완료' : '확인'}
                            </p>
                        </Button>
                    </div>
                )}
                {phoneError && (
                    <p className="text-[clamp(11px, 3vw, 15px)] text-[#D5342B] px-2">
                        {phoneError}
                    </p>
                )}
            </article>
            <article className="flex flex-col gap-2 w-full items-start">
                <label className="text-[clamp(14px,3vw,18px)] font-medium text-[#565656]">
                    상호(법인)명
                </label>
                <Input
                    type="text"
                    className="w-full h-[clamp(44px,3vw,52px)]"
                    inputClassName={'text-[clamp(14px,3vw,18px)] border-[#c1c1c1]'}
                    placeholder="상호(법인)명 입력"
                    value={businessName}
                    maxLength={30}
                    onChange={(e) => {
                        const value = e.target.value
                        if (value.length > 30) {
                            setBusinessName(value.slice(0, 30))
                        } else {
                            setBusinessName(value)
                        }
                        setBusinessName(value)
                        callback('businessName', '')
                    }}
                    onBlur={() => {
                        if (!businessName) {
                            setBusinessError(`상호(법인)명을 입력해주세요.`)
                            return
                        }
                        if (businessName && businessName.length === 0) {
                            setBusinessError(`상호(법인)명을 입력해주세요.`)
                            return
                        }
                        if (businessName.replaceAll(' ', '').length === 0) {
                            setBusinessError(
                                `공백만 입력할 수 없습니다. 상호(법인)명을 입력해주세요`
                            )
                            return
                        }
                        // 정상 케이스
                        if (businessName) {
                            setBusinessError('')
                            callback('businessName', businessName)
                        } else {
                            callback('businessName', '')
                        }
                    }}
                />

                {businessNameError && (
                    <p className="text-[clamp(11px, 3vw, 15px)] text-[#D5342B] px-2">
                        {businessNameError}
                    </p>
                )}
            </article>
            <article className="flex flex-col gap-2 w-full items-start">
                <label className="text-[clamp(14px,3vw,18px)] font-medium text-[#565656]">
                    사업자 등록번호
                </label>
                <div className="flex flex-row gap-2 w-full">
                    <Input
                        type="text"
                        className="w-full h-[clamp(44px,3vw,52px)]"
                        inputClassName={'text-[clamp(14px,3vw,18px)] border-[#c1c1c1]'}
                        placeholder="-없이 숫자 10자리만 입력"
                        value={businessNumber}
                        maxLength={10}
                        onChange={(e) => {
                            const value = e.target.value
                            const onlyNumber = value.replace(/[^0-9]/g, '')
                            if (onlyNumber.length > 10) {
                                setBusinessNumber(onlyNumber.slice(0, 10))
                                return
                            }
                            setBusinessNumber(onlyNumber)
                            setBusinessError('')
                            setCheckBusinessNumber(false)
                        }}
                    />
                    <Button
                        ghost={checkBusinessNumber}
                        className="w-[17%]  h-full flex items-center justify-center"
                        onClick={validateBusinessNumber}
                        disabled={checkBusinessNumber}
                    >
                        <p className="text-[clamp(14px,3vw,20px)]">
                            {checkBusinessNumber ? '확인' : '검사'}
                        </p>
                    </Button>
                </div>

                {businessNumberError && (
                    <p className="text-[clamp(11px, 3vw, 15px)] text-[#D5342B] px-2">
                        {businessNumberError}
                    </p>
                )}
            </article>
            <article className="flex flex-col gap-2 w-full items-start">
                <label className="text-[clamp(14px,3vw,18px)] font-medium text-[#565656]">
                    파일 첨부 <b className="text-[#C1C1C1] font-medium">(png, jpg, jpeg 형식)</b>
                </label>
                <Button
                    loading={businessFileLoading}
                    ghost={!Boolean(businessFileName)}
                    className="flex flex-row gap-3 items-center h-[clamp(44px,3vw,52px)]"
                    onClick={() => businessFileInputRef.current?.click()}
                >
                    <CloudIcon
                        className={`w-[clamp(20px,3vw,24px)] h-[clamp(20px,3vw,24px)] ${
                            !businessFileName ? 'fill-[#565656]' : 'fill-[#ffffff]'
                        }`}
                    />
                    <p
                        className={`text-[clamp(14px,3vw,18px)] font-bold ${
                            !businessFileName ? 'text-[#565656]' : 'text-[#ffffff]'
                        } >`}
                    >
                        {businessFileName ? businessFileName : '사업자등록증 파일 업로드'}
                    </p>
                </Button>
                <Button
                    loading={idCardFileLoading}
                    ghost={!Boolean(idCardFileName)}
                    className="flex flex-row gap-3 items-center h-[clamp(44px,3vw,52px)]"
                    onClick={() => idCardFileInputRef.current?.click()}
                >
                    <CloudIcon
                        className={`w-[clamp(20px,3vw,24px)] h-[clamp(20px,3vw,24px)] ${
                            !idCardFileName ? 'fill-[#565656]' : 'fill-[#ffffff]'
                        }`}
                    />
                    <p
                        className={`text-[clamp(14px,3vw,18px)] font-bold ${
                            !idCardFileName ? 'text-[#565656]' : 'text-[#ffffff]'
                        } >`}
                    >
                        {idCardFileName ? idCardFileName : '신분증 파일 업로드'}
                    </p>
                </Button>
            </article>

            <input
                type="file"
                accept="image/png, image/jpg, image/jpeg"
                style={{ display: 'none' }}
                ref={businessFileInputRef}
                onChange={(e) => uploadHandler(e.target.files, 'business')}
            />
            <input
                type="file"
                accept="image/png, image/jpg, image/jpeg"
                style={{ display: 'none' }}
                ref={idCardFileInputRef}
                onChange={(e) => uploadHandler(e.target.files, 'idcard')}
            />
        </section>
    )
}
